"use client"

import { useEffect, useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { ArrowLeft, Sparkles, Copy, Download, RefreshCw, ChevronDown, ChevronUp, Lightbulb } from "lucide-react"
import { BASE_API_URL } from "../config"

const PromptPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const responseRef = useRef(null)
  const promptRef = useRef(null)

  const selectedType = location.state?.selectedType
  const userResponse = location.state?.responses
  const userEmail = location.state?.userEmail
  const oldPrompt = location.state?.oldPrompt
  const passedSystemPrompt = location.state?.systemprompt
  const costPrompt = location.state?.costPrompt

  const [prompt, setPrompt] = useState("")
  const [systemPrompt, setSystemPrompt] = useState("")
  const [costprompt, setCostPrompt] = useState("")
  const [loading, setLoading] = useState(false)
  const [gptResponse, setGptResponse] = useState("")
  const [promptError, setPromptError] = useState("")
  const [copied, setCopied] = useState(false)
  const [showSystemPrompt, setShowSystemPrompt] = useState(true)
  const [showAnswers, setShowAnswers] = useState(false)
  const [generatingAnimation, setGeneratingAnimation] = useState(false)

  // Pre-fill both prompt fields if they exist
  useEffect(() => {
    if (oldPrompt) setPrompt(oldPrompt)
    if (passedSystemPrompt) setSystemPrompt(passedSystemPrompt)
    if (costPrompt) setCostPrompt(costPrompt)
  }, [oldPrompt, passedSystemPrompt, costPrompt])

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      setPromptError("Prompt is required.")
      return
    }

    setPromptError("")
    setLoading(true)
    setGeneratingAnimation(true)
    setGptResponse("")

    const payload = {
      email: userEmail,
      type: selectedType,
      prompt,
      responses: userResponse,
      systemPrompt,
      costprompt,
    }

    try {
      const res = await fetch(`${BASE_API_URL}/playground/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      })

      const data = await res.json()

      if (data.success && data.gptResponse) {
        setTimeout(() => {
          setGptResponse(data.gptResponse)
          setLoading(false)
          setGeneratingAnimation(false)

          // Scroll to GPT result
          setTimeout(() => {
            responseRef.current?.scrollIntoView({ behavior: "smooth" })
          }, 100)
        }, 800)
      } else {
        setLoading(false)
        setGeneratingAnimation(false)
        alert("Something went wrong while generating the response.")
      }
    } catch (err) {
      setLoading(false)
      setGeneratingAnimation(false)
      console.error(err)
      alert("Submission failed. Try again.")
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(gptResponse)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const downloadResponse = () => {
    const element = document.createElement("a")
    const file = new Blob([gptResponse], { type: "text/markdown" })
    element.href = URL.createObjectURL(file)
    element.download = `ai-response-${new Date().toISOString().slice(0, 10)}.md`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <div className="min-h-screen text-black">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="flex items-center mb-8 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Questions</span>
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/95 backdrop-blur-sm border border-gray-300 rounded-xl p-6 mb-8 shadow-lg"
          ref={promptRef}
        >
          <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Customize Your Prompt
          </h1>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-medium text-purple-600 flex items-center gap-1">
                <Lightbulb className="h-4 w-4" />
                System Prompt
              </div>
              <button
                onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                className="text-sm font-medium text-gray-600 hover:text-black transition-colors flex items-center gap-1"
              >
                {showSystemPrompt ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {showSystemPrompt ? "Hide" : "Show"}
              </button>
            </div>

            {showSystemPrompt && (
              <motion.div
                initial={{ opacity: 1, height: "auto" }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2 mb-4 bg-white rounded-lg p-4 border border-gray-200"
              >
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-black">Instructions for the AI</label>
                  <div className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">Advanced</div>
                </div>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder="Set a system message, like tone or role..."
                  rows={3}
                  className="w-full bg-white border border-gray-300 focus:border-purple-500 focus:ring-purple-500 rounded-lg p-3 text-black resize-none transition-all"
                />
                <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                  <Lightbulb className="h-3 w-3" />
                  This prompt instructs the AI how to respond. Modify to change the AI's behavior and tone.
                </p>
              </motion.div>
            )}

            <div className="space-y-2 bg-white rounded-lg p-4 border border-gray-200">
              <label className="text-xl font-medium text-black">Custom Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Write your custom prompt here..."
                rows={6}
                className={`w-full bg-white border ${
                  promptError ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-purple-500"
                } rounded-lg p-3 text-black resize-none transition-all`}
              />
              {promptError && <p className="text-red-600 text-sm">{promptError}</p>}
              <p className="text-xs text-gray-600">
                This prompt will be combined with your questionnaire answers to generate a personalized response.
              </p>
            </div>
          </div>

          {selectedType === "aiworkflow" && (
            <div className="space-y-2 bg-white rounded-lg p-4 border border-gray-200">
              <label className="text-sm font-medium text-black">Cost Analysis Prompt</label>
              <textarea
                value={costprompt}
                onChange={(e) => setCostPrompt(e.target.value)}
                placeholder="Write your cost analysis prompt here..."
                rows={6}
                className={`w-full bg-white border ${
                  promptError ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-purple-500"
                } rounded-lg p-3 text-black resize-none transition-all`}
              />
              {promptError && <p className="text-red-600 text-sm">{promptError}</p>}
              <p className="text-xs text-gray-600">
                This prompt will be combined with your questionnaire answers to generate a personalized response.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <motion.button
              onClick={handleSubmit}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg hover:shadow-purple-500/25 text-white rounded-lg px-6 py-2 flex items-center gap-2 transition-all"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Response
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* User Answers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white/95 backdrop-blur-sm border border-gray-300 rounded-xl mb-8 overflow-hidden shadow-lg"
        >
          <div
            className="p-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
            onClick={() => setShowAnswers(!showAnswers)}
          >
            <h2 className="text-xl font-semibold">Your Answers</h2>
            <button className="text-gray-600 hover:text-black transition-colors">
              {showAnswers ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
          </div>

          {showAnswers && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="p-6 space-y-6 max-h-96 overflow-y-auto custom-scrollbar"
            >
              {userResponse.map((a, i) => (
                <div key={i} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0 bg-white rounded-lg p-4">
                  <p className="font-medium text-purple-700 text-left">{a.questionText}</p>
                  <p className="text-black mt-1 text-left">{a.answer}</p>
                </div>
              ))}
            </motion.div>
          )}
        </motion.div>

        {/* GPT Output */}
        {(gptResponse || generatingAnimation) && (
          <motion.div
            ref={responseRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/95 backdrop-blur-sm border border-gray-300 rounded-xl p-6 mb-8 scroll-mt-24 shadow-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                AI-Generated Insights
              </h2>
              {gptResponse && (
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-black bg-white border border-gray-300 hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                    {copied ? "Copied!" : "Copy"}
                  </button>
           
                </div>
              )}
            </div>

            {generatingAnimation && !gptResponse ? (
              <div className="bg-white rounded-lg p-6 border border-gray-200 min-h-[300px] flex flex-col justify-center items-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <Sparkles className="h-8 w-8 text-purple-600 animate-pulse" />
                </div>
                <h3 className="text-xl font-medium mb-2">Generating your insights...</h3>
                <p className="text-gray-600 max-w-md text-center">
                  Our AI is analyzing your answers and crafting a personalized response. This may take a moment.
                </p>
                <div className="mt-6 flex space-x-2">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    ></div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="prose prose-lg max-w-none bg-white rounded-lg p-6 border border-gray-200 text-left">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  children={gptResponse}
                  components={{
                    p: ({ children }) => <p className="text-black mb-4 text-left">{children}</p>,
                    h1: ({ children }) => <h1 className="text-purple-600 font-bold mt-6 mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-pink-600 font-bold mt-6 mb-4">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-blue-600 font-bold mt-6 mb-4">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-gray-800 font-semibold mt-6 mb-4">{children}</h4>,
                    h5: ({ children }) => <h5 className="text-gray-800 font-semibold mt-6 mb-4">{children}</h5>,
                    h6: ({ children }) => <h6 className="text-gray-800 font-semibold mt-6 mb-4">{children}</h6>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-purple-500 pl-4 text-gray-700 italic mb-4">
                        {children}
                      </blockquote>
                    ),
                    ul: ({ children }) => <ul className="list-disc ml-5 text-black mb-4">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal ml-5 text-black mb-4">{children}</ol>,
                    li: ({ children }) => <li className="text-black mb-2">{children}</li>,
                    code: ({ children }) => <code className="bg-gray-200 text-black p-1 rounded">{children}</code>,
                    inlineCode: ({ children }) => (
                      <code className="bg-gray-200 text-black p-1 rounded">{children}</code>
                    ),
                    hr: () => <hr className="border-t-2 border-gray-300 my-6" />,
                    strong: ({ children }) => <strong className="font-bold text-black">{children}</strong>,
                    em: ({ children }) => <em className="italic text-black">{children}</em>,
                    del: ({ children }) => <del className="line-through text-gray-600">{children}</del>,
                    table: ({ children }) => <table className="table-auto w-full text-black mb-4">{children}</table>,
                    th: ({ children }) => <th className="border-b border-gray-300 p-2 text-left">{children}</th>,
                    td: ({ children }) => <td className="border-b border-gray-300 p-2">{children}</td>,
                  }}
                />
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default PromptPage
